<script lang="ts">
	import { onMount } from 'svelte';
	import Footer from '$lib/Footer.svelte';
	import Header from '$lib/Header.svelte';
	import Icon from '$lib/Icon.svelte';
	import BrandMark from '$lib/BrandMark.svelte';

	type Platform = 'ios' | 'android' | 'desktop';

	const platforms = [
		{
			id: 'ios' as const,
			label: 'iPhone и iPad',
			caption: 'Safari · iOS / iPadOS',
			icon: 'phone' as const,
			steps: [
				'Откройте web.andresot.ru именно в Safari.',
				'Нажмите «Поделиться» в нижней панели браузера.',
				'Выберите «На экран Домой», затем нажмите «Добавить».'
			]
		},
		{
			id: 'android' as const,
			label: 'Android',
			caption: 'Chrome · Android',
			icon: 'install' as const,
			steps: [
				'Откройте web.andresot.ru в Chrome.',
				'Нажмите «Установить» в подсказке ANDRESOT или откройте меню ⋮.',
				'Подтвердите установку — значок появится среди приложений.'
			]
		},
		{
			id: 'desktop' as const,
			label: 'Компьютер',
			caption: 'Windows · macOS · Linux',
			icon: 'monitor' as const,
			steps: [
				'Откройте web.andresot.ru в Chrome или Edge.',
				'Нажмите значок установки справа в адресной строке.',
				'Подтвердите установку — ANDRESOT откроется в отдельном окне.'
			]
		}
	];

	/*
	 * Шаги для приложения Android — пока APK опубликован. Не загрузился манифест —
	 * остаётся установка из браузера, а не шаги про несуществующую кнопку.
	 */
	const androidApkSteps = [
		'Нажмите «Скачать для Android» — файл весит меньше мегабайта.',
		'Откройте скачанный файл и разрешите установку из браузера.',
		'Нажмите «Установить» — значок ANDRESOT появится среди приложений.'
	];

	let activePlatform = $state<Platform>('ios');
	let hydrated = $state(false);
	let windowsRelease = $state<{ version: string; file: string; size: number } | null>(null);
	let androidRelease = $state<{ version: string; file: string; size: number } | null>(null);

	function selectPlatform(platform: Platform, focus = false): void {
		activePlatform = platform;
		if (focus) requestAnimationFrame(() => document.getElementById(`tab-${platform}`)?.focus());
	}

	function movePlatform(event: KeyboardEvent, current: Platform): void {
		const index = platforms.findIndex((platform) => platform.id === current);
		if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
		event.preventDefault();
		if (event.key === 'Home') return selectPlatform(platforms[0].id, true);
		if (event.key === 'End') return selectPlatform(platforms[platforms.length - 1].id, true);
		const delta = event.key === 'ArrowRight' ? 1 : -1;
		selectPlatform(platforms[(index + delta + platforms.length) % platforms.length].id, true);
	}

	onMount(() => {
		void fetch('https://download.andresot.ru/windows/stable/release.json', { cache: 'no-store' })
			.then((response) => (response.ok ? response.json() : null))
			.then((value) => {
				if (
					value &&
					/^\d+\.\d+\.\d+$/.test(value.version) &&
					/^ANDRESOT-Setup-\d+\.\d+\.\d+-x64\.exe$/.test(value.file) &&
					Number.isSafeInteger(value.size)
				)
					windowsRelease = value;
			})
			.catch(() => undefined);
		void fetch('https://download.andresot.ru/android/stable/release.json', { cache: 'no-store' })
			.then((response) => (response.ok ? response.json() : null))
			.then((value) => {
				if (
					value &&
					/^\d+\.\d+\.\d+$/.test(value.version) &&
					value.file === `ANDRESOT-${value.version}.apk` &&
					Number.isSafeInteger(value.size)
				)
					androidRelease = value;
			})
			.catch(() => undefined);
		const agent = navigator.userAgent.toLowerCase();
		if (/android/.test(agent)) selectPlatform('android');
		else if (!/iphone|ipad|ipod/.test(agent)) selectPlatform('desktop');
		hydrated = true;
	});
</script>

<svelte:head>
	<title>Установить ANDRESOT на телефон или компьютер</title>
	<meta
		name="description"
		content="Установите ANDRESOT для Windows с автоматическими обновлениями или добавьте веб-приложение на iPhone, Android, macOS и Linux."
	/>
	<link rel="canonical" href="https://andresot.ru/download/" />
	<meta property="og:type" content="website" />
	<meta property="og:site_name" content="ANDRESOT" />
	<meta property="og:locale" content="ru_RU" />
	<meta property="og:url" content="https://andresot.ru/download/" />
	<meta property="og:title" content="Установить ANDRESOT" />
	<meta property="og:description" content="Инструкции для iPhone, Android и компьютера." />
	<meta property="og:image" content="https://andresot.ru/og-andresot.png" />
	<meta property="og:image:width" content="1200" />
	<meta property="og:image:height" content="630" />
	<meta name="twitter:card" content="summary_large_image" />
</svelte:head>

<Header />

<main id="main-content">
	<section class="page download-hero">
		<div class="download-copy">
			<div class="eyebrow">На вашем устройстве</div>
			<h1>ANDRESOT всегда <span class="accent-text">под рукой</span></h1>
			<p>
				Добавьте ANDRESOT на главный экран или рабочий стол. Открывайте свои чаты одним нажатием — в
				отдельном окне приложения.
			</p>
			<div class="hero-actions">
				<a class="button" href="https://web.andresot.ru"
					>Открыть ANDRESOT <Icon name="external" size={18} /></a
				>
				<a class="button secondary" href="#instructions">Показать инструкцию</a>
			</div>
		</div>
		<div class="install-preview" aria-label="Одно приложение для ваших устройств">
			<div class="install-app-icon"><BrandMark size={104} decorative /></div>
			<strong>Ваше общение — рядом</strong>
			<p>Один аккаунт на телефоне и компьютере</p>
			<div class="install-devices" aria-label="Поддерживаемые устройства">
				<span>iPhone и iPad</span><span>Android</span><span>Компьютер</span>
			</div>
		</div>
	</section>

	<section class="page section install-section" id="instructions">
		<div class="section-kicker">Пошаговая установка</div>
		<div class="section-heading split-heading">
			<h2>Выберите своё устройство</h2>
			<p>Три простых шага в браузере — и значок ANDRESOT появится на вашем устройстве.</p>
		</div>

		<div class="platform-tabs" role="tablist" aria-label="Устройство">
			{#each platforms as platform (platform.id)}
				<button
					type="button"
					role="tab"
					aria-selected={activePlatform === platform.id}
					aria-controls={`panel-${platform.id}`}
					id={`tab-${platform.id}`}
					tabindex={activePlatform === platform.id ? 0 : -1}
					disabled={!hydrated}
					onclick={() => selectPlatform(platform.id)}
					onkeydown={(event) => movePlatform(event, platform.id)}
				>
					<Icon name={platform.icon} size={21} />
					<span><strong>{platform.label}</strong><small>{platform.caption}</small></span>
				</button>
			{/each}
		</div>

		{#each platforms as platform (platform.id)}
			{#if activePlatform === platform.id}
				<div
					class="platform-panel"
					role="tabpanel"
					id={`panel-${platform.id}`}
					aria-labelledby={`tab-${platform.id}`}
				>
					<div class="platform-steps">
						{#each platform.id === 'android' && androidRelease ? androidApkSteps : platform.steps as step, index (step)}
							<div class="install-step">
								<span>{String(index + 1).padStart(2, '0')}</span>
								<p>{step}</p>
							</div>
						{/each}
					</div>
					<div
						class={(platform.id === 'desktop' && windowsRelease) ||
						(platform.id === 'android' && androidRelease)
							? 'platform-result windows-result'
							: 'platform-result'}
					>
						<div class="result-icon"><Icon name={platform.icon} size={32} /></div>
						<strong>{platform.label}</strong>
						<p>После установки появится собственный значок ANDRESOT и отдельное окно приложения.</p>
						{#if platform.id === 'desktop' && windowsRelease}
							<a
								class="button"
								href={`https://download.andresot.ru/windows/stable/${windowsRelease.file}`}
								>Скачать для Windows · {windowsRelease.version} · {Math.ceil(
									windowsRelease.size / 1048576
								)} МБ <Icon name="arrow" size={18} /></a
							>
						{:else if platform.id === 'android' && androidRelease}
							<a
								class="button"
								href={`https://download.andresot.ru/android/stable/${androidRelease.file}`}
								>Скачать для Android · {androidRelease.version} · {Math.max(
									1,
									Math.ceil(androidRelease.size / 1048576)
								)} МБ <Icon name="arrow" size={18} /></a
							>
							<a class="button secondary" href="https://web.andresot.ru"
								>Или открыть в браузере <Icon name="external" size={18} /></a
							>
						{:else}
							<a class="button" href="https://web.andresot.ru"
								>Открыть в браузере <Icon name="arrow" size={18} /></a
							>
						{/if}
					</div>
				</div>
			{/if}
		{/each}
	</section>

	<section class="page distribution-note">
		<div class="icon-box"><Icon name="shield" size={24} /></div>
		<div>
			<h2>Установка ANDRESOT</h2>
			<p>
				ANDRESOT устанавливается как веб-приложение. Для установки используйте инструкцию выше. Для
				Windows доступно отдельное приложение с автоматическими обновлениями, для Android 7.0 и
				новее — приложение с уведомлениями и звонками, которое тоже обновляется само. На iPhone и
				других компьютерах используйте установку из браузера.
			</p>
		</div>
	</section>

	<section class="page device-benefits" aria-label="Преимущества установки">
		<article>
			<Icon name="browser" size={23} />
			<h3>Тот же интерфейс</h3>
			<p>Переписка и настройки остаются одинаковыми в браузере и установленном окне.</p>
		</article>
		<article>
			<Icon name="install" size={23} />
			<h3>Автоматические обновления</h3>
			<p>Новая версия загружается безопасно, без ручного скачивания установщика.</p>
		</article>
		<article>
			<Icon name="at" size={23} />
			<h3>Один аккаунт</h3>
			<p>Войдите с тем же логином на телефоне и компьютере.</p>
		</article>
	</section>

	<section class="page cta-section compact-cta">
		<div class="section-kicker">Готово к работе</div>
		<h2>Откройте ANDRESOT</h2>
		<p>Сначала попробуйте в браузере, а установить приложение можно позже.</p>
		<div class="hero-actions">
			<a class="button" href="https://web.andresot.ru"
				>Перейти в приложение <Icon name="arrow" size={19} /></a
			>
			<a class="button secondary" href="/">Вернуться на главную</a>
		</div>
	</section>
</main>

<Footer />
